import html2canvas from "html2canvas";

function waitForNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function formatCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return numeric.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPurchaseType(value) {
  const key = String(value || "").trim().toLowerCase();
  if (!key) return "";
  if (key === "personal_residence") return "Personal Residence";
  if (key === "investment_property") return "Investment Property";
  return String(value);
}

function buildBuyerName(offer) {
  const buyer1 = String(
    offer?.buyer_name || offer?.buyers_combined || offer?.buyer_1__full_name || ""
  ).trim();
  const buyer2 = String(offer?.buyer_2_full_name || "").trim();
  if (buyer1 && buyer2 && !buyer1.includes(" and ")) return `${buyer1} and ${buyer2}`;
  return buyer1 || buyer2;
}

function buildOfferNotes(offer) {
  return [offer?.offer_notes_1, offer?.lender_notes, offer?.notes]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join("\n");
}

function normalizeCashValue(offer) {
  if (offer?.cash === "Yes" || offer?.cash === "No") return offer.cash;
  if (offer?.cash === true || offer?.cash === "true" || offer?.cash_purchase) return "Yes";
  if (
    offer?.cash === false ||
    offer?.cash === "false" ||
    offer?.cash_purchase === false
  ) {
    return "No";
  }
  return "";
}

function buildTemplateData(offer = {}) {
  const address1 = String(offer.address_1 || offer.buyer_current_address || "").trim();
  const address2 = String(offer.address_2 || "").trim();
  const unitNumber = String(offer.unit_number || offer.contract_unit_number || "").trim();
  const priceValue =
    offer.final_price ?? offer.list_price ?? offer.base_price ?? offer.price ?? "";

  return {
    ...offer,
    project_id: String(offer.project_id || "Fusion").trim() || "Fusion",
    buyer_name: buildBuyerName(offer),
    unit_number: unitNumber,
    address_1: address1,
    address_2: address2,
    city: String(offer.city || offer.buyer_current_city || "").trim(),
    state: String(offer.state || offer.buyer_current_state || "").trim(),
    zip_code: String(offer.zip_code || offer.buyer_current_zip || "").trim(),
    phone_number_1: String(offer.phone_number_1 || offer.buyer_mobile_phone || "").trim(),
    phone_number_2: String(offer.phone_number_2 || "").trim(),
    phone_number_3: String(offer.phone_number_3 || "").trim(),
    email_1: String(offer.email_1 || offer.buyer1_email || offer.buyer_1_email || "").trim(),
    email_2: String(offer.email_2 || offer.buyer_2_email || "").trim(),
    email_3: String(offer.email_3 || "").trim(),
    lender: String(offer.lender || offer.primary_lender || "").trim(),
    loan_officer: String(
      offer.loan_officer || offer.primary_loan_officer_full_name || offer.primary_loan_officer || ""
    ).trim(),
    l_o_contact_email: String(
      offer.l_o_contact_email ||
        offer.loan_officer_email ||
        offer.primary_loan_officer_email ||
        offer.broker_email ||
        offer.referring_agent_email ||
        ""
    ).trim(),
    l_o_phone: String(
      offer.l_o_phone || offer.loan_officer_phone || offer.broker_phone || ""
    ).trim(),
    broker_name: String(offer.broker_name || offer.referring_agent_full_name || "").trim(),
    brokerage: String(offer.brokerage || offer.agent_brokerage || "").trim(),
    broker_email: String(offer.broker_email || offer.referring_agent_email || "").trim(),
    broker_phone: String(offer.broker_phone || "").trim(),
    cash: normalizeCashValue(offer),
    priceFmt: String(offer.priceFmt || "").trim() || formatCurrency(priceValue),
    purchase_type: formatPurchaseType(offer.purchase_type),
    offer_notes_1: buildOfferNotes(offer),
    buyer_notes: String(offer.buyer_notes || "").trim(),
    add_notes: String(offer.add_notes || offer.notes || "").trim(),
    bldg: String(offer.building_info || offer.bldg || "").trim(),
    plan: String(offer.plan_info || offer.plan_type || offer.plan || "").trim(),
    addr: String(offer.addr || offer.address || `${address1} ${address2}`.trim()).trim(),
  };
}

async function fetchTemplate(projectId) {
  const candidates = [
    `/pdf-templates/${encodeURIComponent(projectId)}/offer-template.html`,
    "/pdf-templates/offer-template.html",
    "/pdf-templates/Fusion/offer-template.html",
  ];

  for (const path of candidates) {
    const res = await fetch(path, { cache: "no-store" });
    if (res.ok) {
      return res.text();
    }
  }

  throw new Error(`No offer PDF template found for project ${projectId}.`);
}

function renderOfferTemplate(template, templateData) {
  let html = template;
  for (const [key, value] of Object.entries(templateData)) {
    const safeValue = value == null ? "" : String(value);
    const pattern = new RegExp(`{{\\s*${key}\\s*}}`, "g");
    html = html.replace(pattern, () => safeValue);
  }

  return html.replace(
    /Home Details \(From [^)]+\)/g,
    `Home Details (From ${templateData.project_id})`
  );
}

async function waitForDocumentAssets(doc) {
  const images = Array.from(doc.images || []);
  await Promise.all(
    images.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    })
  );
  await waitForNextPaint();
}

function buildPdfFromJpeg({
  dataUrl,
  imageWidth,
  imageHeight,
  pageWidth = 612,
  pageHeight = 792,
  margin = 18,
}) {
  const base64 = dataUrl.split(",")[1] || "";
  const binary = window.atob(base64);
  const imageBytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    imageBytes[index] = binary.charCodeAt(index);
  }

  const availableWidth = pageWidth - margin * 2;
  const availableHeight = pageHeight - margin * 2;
  const scale = Math.min(availableWidth / imageWidth, availableHeight / imageHeight);
  const drawWidth = imageWidth * scale;
  const drawHeight = imageHeight * scale;
  const drawX = (pageWidth - drawWidth) / 2;
  const drawY = (pageHeight - drawHeight) / 2;

  const content = `q\n${drawWidth.toFixed(2)} 0 0 ${drawHeight.toFixed(
    2
  )} ${drawX.toFixed(2)} ${drawY.toFixed(2)} cm\n/Im0 Do\nQ`;

  const encoder = new TextEncoder();
  const parts = [encoder.encode("%PDF-1.4\n")];
  const offsets = [0];
  let position = parts[0].length;

  function pushChunk(chunk) {
    parts.push(chunk);
    position += chunk.length;
  }

  function pushText(text) {
    pushChunk(encoder.encode(text));
  }

  function addObject(objectNumber, chunks) {
    offsets[objectNumber] = position;
    pushText(`${objectNumber} 0 obj\n`);
    chunks.forEach((chunk) =>
      pushChunk(typeof chunk === "string" ? encoder.encode(chunk) : chunk)
    );
    pushText("\nendobj\n");
  }

  addObject(1, ["<< /Type /Catalog /Pages 2 0 R >>"]);
  addObject(2, ["<< /Type /Pages /Count 1 /Kids [3 0 R] >>"]);
  addObject(3, [
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`,
  ]);
  addObject(4, [
    `<< /Type /XObject /Subtype /Image /Width ${imageWidth} /Height ${imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imageBytes.length} >>\nstream\n`,
    imageBytes,
    "\nendstream",
  ]);
  const contentBytes = encoder.encode(content);
  addObject(5, [
    `<< /Length ${contentBytes.length} >>\nstream\n`,
    contentBytes,
    "\nendstream",
  ]);

  const xrefOffset = position;
  pushText("xref\n0 6\n0000000000 65535 f \n");
  for (let objectNumber = 1; objectNumber <= 5; objectNumber += 1) {
    pushText(`${String(offsets[objectNumber]).padStart(10, "0")} 00000 n \n`);
  }
  pushText(`trailer << /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(parts, { type: "application/pdf" });
}

export async function generateOfferPdf(offer = {}) {
  const templateData = buildTemplateData(offer);
  const template = await fetchTemplate(templateData.project_id);
  const html = renderOfferTemplate(template, templateData);

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = "850px";
  iframe.style.height = "1400px";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";

  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error("Unable to initialize PDF document.");

    doc.open();
    doc.write(html);
    doc.close();

    await waitForDocumentAssets(doc);

    const target = doc.body;
    const width = Math.max(target.scrollWidth, doc.documentElement.scrollWidth, 816);
    const height = Math.max(target.scrollHeight, doc.documentElement.scrollHeight, 1056);

    const canvas = await html2canvas(target, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
    });

    return buildPdfFromJpeg({
      dataUrl: canvas.toDataURL("image/jpeg", 0.94),
      imageWidth: canvas.width,
      imageHeight: canvas.height,
    });
  } finally {
    document.body.removeChild(iframe);
  }
}
